"use client"

import { useState, useEffect } from "react"
import { createPortal } from "react-dom"
import { toast } from "sonner"
import { X, CheckCircle2, ArchiveX, Calendar, Pencil, ClipboardCheck } from "@/lib/icons"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import { useEscapeKey } from "@/hooks/useEscapeKey"

const DESCARTE_RAZONES = [
  "No tiene interés",
  "Presupuesto insuficiente",
  "Eligió otra institución",
  "No cumple requisitos",
  "No contesta llamadas/mensajes",
  "Fuera del área de cobertura",
  "Otro",
]

export function LeadStatusModal({ open, onClose, lead, targetStatus, onConfirm }) {
  useEscapeKey(open, onClose)
  const [formData, setFormData] = useState({
    fecha: "",
    notas: "",
    razon: "",
    otraRazon: "",
    aplicoExamen: false,
    examenAprobado: null,
  })

  useEffect(() => {
    if (open && lead) {
      // Reset form when modal opens
      setFormData({
        fecha: "",
        notas: lead?.notas || "",
        razon: "",
        otraRazon: "",
        aplicoExamen: false,
        examenAprobado: null,
      })
    }
  }, [open, lead])

  const handleClose = () => {
    setFormData({
      fecha: "",
      notas: "",
      razon: "",
      otraRazon: "",
      aplicoExamen: false,
      examenAprobado: null,
    })
    onClose()
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (targetStatus === "agendar_cita" && !formData.fecha) {
      toast.error("Fecha requerida", { description: "Selecciona una fecha y hora para la cita." })
      return
    }

    if (targetStatus === "cierre_inscripcion" && formData.examenAprobado === true && !formData.fecha) {
      toast.error("Fecha requerida", { description: "Selecciona la fecha de inscripción." })
      return
    }

    onConfirm(formData)
  }

  const isValid = () => {
    if (targetStatus === "cierre_inscripcion") {
      return formData.fecha.trim() !== "" && formData.examenAprobado !== null
    }
    if (targetStatus === "agendar_cita") {
      return formData.fecha.trim() !== ""
    }
    if (targetStatus === "archivado") {
      return formData.razon && (formData.razon !== "Otro" || (formData.otraRazon || "").trim())
    }
    if (targetStatus === "aplicar_examen") {
      return formData.fecha.trim() !== ""
    }
    if (targetStatus === "entrevista") {
      return true // Solo requiere confirmación, fecha/notas opcionales
    }
    return false
  }

  const canConfirm = isValid()

  if (!open || !lead) return null

  // Determinar el título y el ícono según el targetStatus
  const getModalConfig = () => {
    switch (targetStatus) {
      case "cierre_inscripcion":
        return {
          title: "Cerrar Inscripción",
          subtitle: "Confirma el resultado del examen antes de finalizar",
          icon: CheckCircle2,
          iconGradient: "from-emerald-500 to-green-500",
          buttonGradient: "from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700",
        }
      case "agendar_cita":
        return {
          title: "Cita Agendada",
          subtitle: "Programa una reunión con el lead",
          icon: Calendar,
          iconGradient: "from-blue-500 to-cyan-500",
          buttonGradient: "from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700",
        }
      case "entrevista":
        return {
          title: "Entrevista Previa",
          subtitle: "Activa la entrevista previa al examen de admisión",
          icon: ClipboardCheck,
          iconGradient: "from-violet-500 to-purple-500",
          buttonGradient: "from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700",
        }
      case "aplicar_examen":
        return {
          title: "Aplicar Examen",
          subtitle: "Confirma que el lead aplicará examen (requiere entrevista previa)",
          icon: Pencil,
          iconGradient: "from-purple-500 to-pink-500",
          buttonGradient: "from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700",
        }
      case "archivado":
        return {
          title: "Archivar Lead",
          subtitle: "Registra por qué se archiva este lead",
          icon: ArchiveX,
          iconGradient: "from-red-500 to-pink-500",
          buttonGradient: "from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700",
        }
      default:
        return {
          title: "Actualizar Lead",
          subtitle: "",
          icon: CheckCircle2,
          iconGradient: "from-slate-500 to-slate-600",
          buttonGradient: "from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800",
        }
    }
  }

  const config = getModalConfig()
  const ModalIcon = config.icon

  const renderContent = () => {
    switch (targetStatus) {
      case "agendar_cita":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                📅 Fecha y hora de la cita *
              </label>
              <input
                type="datetime-local"
                value={formData.fecha}
                onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                min={new Date().toISOString().slice(0, 16)}
                required
                className="w-full px-4 py-3 rounded-xl border-2 border-indigo-200 dark:border-indigo-800 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
              />
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                💡 Esta fecha se guardará automáticamente y se enviará recordatorio al lead
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                📝 Notas adicionales (opcional)
              </label>
              <textarea
                value={formData.notas}
                onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                rows={3}
                placeholder="Ej: Llevar acta de nacimiento, CURP, comprobante de domicilio..."
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white resize-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
              />
            </div>
          </div>
        )

      case "cierre_inscripcion":
        return (
          <div className="space-y-4">
            <div className="rounded-xl bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30 p-4 border-2 border-indigo-200 dark:border-indigo-800">
              <p className="text-sm font-semibold text-indigo-900 dark:text-indigo-100 mb-3">
                🎯 ¿El lead aprobó el examen de admisión?
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, examenAprobado: true })}
                  className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${formData.examenAprobado === true
                    ? "bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-lg scale-105"
                    : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-2 border-slate-200 dark:border-slate-700 hover:border-emerald-500"
                    }`}
                >
                  ✅ Sí, aprobado
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, examenAprobado: false })}
                  className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${formData.examenAprobado === false
                    ? "bg-gradient-to-r from-rose-500 to-red-600 text-white shadow-lg scale-105"
                    : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-2 border-slate-200 dark:border-slate-700 hover:border-rose-500"
                    }`}
                >
                  ❌ No aprobado
                </button>
              </div>
            </div>

            {formData.examenAprobado === true && (
              <div className="animate-in fade-in slide-in-from-top-4 duration-300">
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                  📅 Fecha de inscripción *
                </label>
                <input
                  type="date"
                  value={formData.fecha}
                  onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                  required
                  className="w-full px-4 py-3 rounded-xl border-2 border-emerald-200 dark:border-emerald-800 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                />
                <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">
                  🎉 ¡Excelente! Registra la fecha en que se formalizó la inscripción
                </p>
              </div>
            )}

            {formData.examenAprobado === false && (
              <div className="rounded-xl bg-rose-50 dark:bg-rose-950/30 p-4 border-2 border-rose-200 dark:border-rose-800 animate-in fade-in slide-in-from-top-4 duration-300">
                <p className="text-sm text-rose-700 dark:text-rose-300 mb-2">
                  ⚠️ El lead será archivado automáticamente
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                📝 Notas {formData.examenAprobado === false ? "(motivo de rechazo)" : "(opcional)"}
              </label>
              <textarea
                value={formData.notas}
                onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                rows={3}
                placeholder={
                  formData.examenAprobado === false
                    ? "Ej: No alcanzó el puntaje mínimo, decidió no inscribirse..."
                    : "Ej: Pago completo, documentación entregada..."
                }
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white resize-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
              />
            </div>
          </div>
        )

      // Formulario para Entrevista Previa
      case "entrevista":
        return (
          <div className="space-y-4">
            <div className="rounded-xl bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30 p-4 border-2 border-violet-200 dark:border-violet-800">
              <p className="text-sm font-semibold text-violet-900 dark:text-violet-100 mb-2">
                📋 Activar entrevista previa
              </p>
              <p className="text-xs text-violet-700 dark:text-violet-300">
                Al confirmar, el lead pasará a la etapa de entrevista previa al examen de admisión.
                El bot será desactivado para que la asesora tome control total de la conversación.
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                📅 Fecha de entrevista (opcional)
              </label>
              <input
                type="datetime-local"
                value={formData.fecha}
                onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                min={new Date().toISOString().slice(0, 16)}
                className="w-full px-4 py-3 rounded-xl border-2 border-violet-200 dark:border-violet-800 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                📝 Notas de la entrevista (opcional)
              </label>
              <textarea
                value={formData.notas}
                onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                rows={3}
                placeholder="Ej: Papás interesados, mencionaron que vienen de otra escuela..."
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white resize-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20"
              />
            </div>
          </div>
        )

      // Formulario para Aplicar Examen
      case "aplicar_examen":
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fecha-examen" className="flex items-center gap-2 text-sm font-semibold">
                <Calendar className="h-4 w-4 text-purple-500" />
                Fecha y hora del examen *
              </Label>
              <Input
                id="fecha-examen"
                type="datetime-local"
                value={formData.fecha}
                onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                className="h-11 rounded-xl"
                required
              />
              <p className="text-xs text-slate-500">Selecciona cuándo presentará el examen este lead.</p>
            </div>
            <div className="space-y-3">
              <label className="flex items-center gap-3 p-4 rounded-xl border-2 border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-950/30 cursor-pointer hover:border-purple-400 transition-all">
                <input
                  type="checkbox"
                  checked={formData.aplicoExamen}
                  onChange={(e) => setFormData({ ...formData, aplicoExamen: e.target.checked })}
                  className="h-5 w-5 rounded text-purple-600"
                />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-purple-900 dark:text-purple-100">
                    El lead aplicó el examen
                  </p>
                  <p className="text-xs text-purple-700 dark:text-purple-300">
                    Marca esta casilla para confirmar que el examen fue aplicado
                  </p>
                </div>
              </label>
            </div>
            <div className="rounded-xl bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 p-4">
              <p className="text-sm text-purple-800 dark:text-purple-200 font-medium">
                ✏️ Este lead pasará a la etapa de examen aplicado
              </p>
            </div>
          </div>
        )

      // Formulario para Archivar
      case "archivado":
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Razón del archivo *</Label>
              <div className="grid gap-2 max-h-60 overflow-y-auto">
                {DESCARTE_RAZONES.map((razon) => (
                  <label
                    key={razon}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${formData.razon === razon
                      ? "border-red-500 bg-red-50 dark:bg-red-950/30"
                      : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                      }`}
                  >
                    <input
                      type="radio"
                      name="razon"
                      value={razon}
                      checked={formData.razon === razon}
                      onChange={(e) => setFormData({ ...formData, razon: e.target.value })}
                      className="h-4 w-4 text-red-500"
                    />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{razon}</span>
                  </label>
                ))}
              </div>
            </div>

            {formData.razon === "Otro" && (
              <div className="space-y-2">
                <Label htmlFor="otra-razon" className="text-sm font-semibold">
                  Especifica la razón *
                </Label>
                <Input
                  id="otra-razon"
                  value={formData.otraRazon}
                  onChange={(e) => setFormData({ ...formData, otraRazon: e.target.value })}
                  placeholder="Escribe aquí la razón..."
                  className="h-11 rounded-xl"
                  required
                />
              </div>
            )}
          </div>
        )

      default:
        return null
    }
  }

  const content = renderContent()

  const modalNode = (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-200">
        <button
          onClick={handleClose}
          className="absolute top-2.5 right-2.5 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors z-10"
        >
          <X className="h-5 w-5 text-slate-500" />
        </button>

        <div className="p-4 sm:p-5 space-y-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2.5 pr-8">
              <div className={`flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br ${config.iconGradient} shadow`}>
                <ModalIcon className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">{config.title}</h3>
                <p className="text-xs text-slate-600 dark:text-slate-400 truncate">{lead?.nombre_tutor}</p>
                {config.subtitle && <p className="text-[10px] text-slate-500">{config.subtitle}</p>}
              </div>
            </div>
          </div>

          {content}

          <div className="flex gap-2 pt-3 border-t border-slate-200 dark:border-slate-800">
            <Button variant="outline" onClick={handleClose} className="flex-1 rounded-lg h-9 text-sm">
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!canConfirm}
              className={`flex-1 rounded-lg h-9 text-sm bg-gradient-to-r ${config.buttonGradient} text-white`}
            >
              Confirmar
            </Button>
          </div>
        </div>
      </div>
    </div>
  )

  return typeof document !== "undefined" ? createPortal(modalNode, document.body) : modalNode
}

